import { Client } from '@app/core/entity/client';
import { ClientService } from '@app/core/services/clients.service';
import { AppState } from '@app/core/state/appState';
import { FirmSelectors } from '@app/core/state/firm/firm.selectors';
import { FirmType } from '@app/core/state/types';
import { TranslocoService } from '@jsverse/transloco';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DialogService } from 'primeng/dynamicdialog';
import { filter, first, map, mergeMap, Observable, switchMap, withLatestFrom } from 'rxjs';
import { TranslatableStringType } from 'src/app/core/i18n/types';
import { NewClientDialog } from '../components/new-client/new.client.dialog';
import { NewClientDialogConfig, NewClientDialogResult } from '../components/new-client/types';

const newClientDialogTranslations$ = (translationService: TranslocoService) =>
  translationService.selectTranslation('newClientDialog').pipe(filter(Boolean), first());

const firm$ = (store: Store<AppState>) => store.select(FirmSelectors.selectFirm);

const showNewClientDialog = ({
  dialogService,
  client,
  firm,
  tabToOpen,
  translations,
  updateEmitter,
}: {
  dialogService: DialogService;
  client: Client;
  firm: FirmType;
  tabToOpen: string;
  translations: Record<string, TranslatableStringType>;
  updateEmitter?: (updated: boolean) => void;
}): Observable<NewClientDialogResult> => {
  const data: NewClientDialogConfig = { client, firm, tabToOpen, updateEmitter };
  const ref = dialogService.open(NewClientDialog, {
    data,
    header: (client?.archived ? `${translations.archived} - ` : '') + (client?.name || translations.newClient),
    width: '1255px',
    style: { minHeight: 'min(732px, 90vh)', maxHeight: 'min(872px, 90vh)' },
    modal: true,
    styleClass: 'new-client-dialog tabbed-dialog',
  });

  return ref.onClose.pipe(first());
};

export const showClientDialog = ({
  clientService,
  dialogService,
  translationService,
  id,
  store,
  tabToOpen = '',
  updateEmitter,
}: {
  clientService: ClientService;
  dialogService: DialogService;
  translationService: TranslocoService;
  id: number;
  store: Store<AppState>;
  tabToOpen?: string;
  updateEmitter?: (updated: boolean) => void;
}): Observable<NewClientDialogResult> => {
  if (id === -1) {
    const client = new Client('');
    return newClientDialogTranslations$(translationService).pipe(
      concatLatestFrom(() => firm$(store)),
      first(),
      mergeMap(([translations, firm]) => showNewClientDialog({ dialogService, client, firm, tabToOpen, translations, updateEmitter })),
    );
  }

  return clientService.getClientById(id).pipe(
    switchMap((client) =>
      newClientDialogTranslations$(translationService).pipe(map((translations) => ({ client, translations }))),
    ),
    withLatestFrom(firm$(store)),
    mergeMap(([{ client, translations }, firm]) =>
      showNewClientDialog({ dialogService, client, firm, tabToOpen, translations, updateEmitter }),
    ),
  );
};
